import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";
import {
	Upload,
	Loader2,
	Power,
	PowerOff,
	RotateCcw,
	RefreshCw,
	Trash2,
	ScrollText,
	PackageOpen,
	ShieldCheck,
	History,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Módulos que chegam como pacote (.zip assinado) e rodam ao lado do sistema,
// como o motor de ligações. Diferente da aba "À venda": lá se decide quem
// pode usar; aqui, se o código está presente e de pé neste servidor.

const ESTADOS = {
	rodando: { rotulo: "Rodando", cor: "bg-emerald-500" },
	iniciando: { rotulo: "Iniciando", cor: "bg-amber-500 animate-pulse" },
	reiniciando: { rotulo: "Reiniciando", cor: "bg-amber-500 animate-pulse" },
	parado: { rotulo: "Parado", cor: "bg-muted-foreground" },
	erro: { rotulo: "Com erro", cor: "bg-destructive" },
};

const dataHora = (v) => (v ? new Date(v).toLocaleString("pt-BR") : "—");

const Instalados = () => {
	const [dados, setDados] = useState(null);
	const [enviando, setEnviando] = useState(false);
	const [ocupado, setOcupado] = useState(null);
	const [logs, setLogs] = useState(null);
	const [remover, setRemover] = useState(null);
	const [apagarDados, setApagarDados] = useState(false);
	const arquivoRef = useRef(null);

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get("/installed-modules");
			setDados(data);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	// Enquanto algum módulo está subindo, acompanha até assentar.
	const transitorio = dados?.modulos?.some((m) =>
		["iniciando", "reiniciando"].includes(m.situacao?.estado)
	);
	useEffect(() => {
		if (!transitorio) return undefined;
		const t = setInterval(carregar, 2000);
		return () => clearInterval(t);
	}, [transitorio, carregar]);

	const enviar = async (arquivo) => {
		if (!arquivo) return;
		const form = new FormData();
		form.append("file", arquivo);
		setEnviando(true);
		try {
			const { data } = await api.post("/installed-modules", form, {
				timeout: 10 * 60 * 1000,
			});
			toast.success(`${data.name} ${data.version} instalado.`);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setEnviando(false);
		if (arquivoRef.current) arquivoRef.current.value = "";
	};

	const acao = async (modulo, caminho, mensagem) => {
		setOcupado(`${modulo.key}:${caminho}`);
		try {
			await api.post(`/installed-modules/${modulo.key}/${caminho}`);
			if (mensagem) toast.success(mensagem);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setOcupado(null);
	};

	const verLogs = async (modulo) => {
		try {
			const { data } = await api.get(`/installed-modules/${modulo.key}/logs`);
			setLogs({ modulo, linhas: data.linhas });
		} catch (err) {
			toastError(err);
		}
	};

	const confirmarRemocao = async () => {
		try {
			await api.delete(`/installed-modules/${remover.key}`, {
				params: { apagarDados },
			});
			toast.success(`${remover.name} removido.`);
			setRemover(null);
			setApagarDados(false);
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const modulos = dados?.modulos || [];

	return (
		<div className="space-y-4">
			<div
				className={cn(
					"flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed bg-card p-6 text-center transition-colors",
					enviando && "opacity-70"
				)}
				onDragOver={(e) => e.preventDefault()}
				onDrop={(e) => {
					e.preventDefault();
					enviar(e.dataTransfer.files?.[0]);
				}}
			>
				{enviando ? (
					<Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
				) : (
					<Upload className="h-7 w-7 text-muted-foreground" />
				)}
				<p className="text-sm font-medium">
					{enviando ? "Conferindo e instalando…" : "Instalar ou atualizar módulo"}
				</p>
				<p className="max-w-md text-xs text-muted-foreground">
					Arraste o pacote <code>.zip</code> aqui ou escolha o arquivo. Só entram
					pacotes com assinatura válida; subir a mesma chave com versão nova
					atualiza e guarda a anterior para voltar.
				</p>
				<input
					ref={arquivoRef}
					type="file"
					accept=".zip,application/zip"
					className="hidden"
					onChange={(e) => enviar(e.target.files?.[0])}
				/>
				<Button
					className="mt-1 h-9"
					disabled={enviando}
					onClick={() => arquivoRef.current?.click()}
				>
					<PackageOpen className="h-4 w-4" />
					Escolher pacote
				</Button>
				{dados?.plataforma && (
					<p className="text-[11px] text-muted-foreground">
						Este servidor: <code>{dados.plataforma}</code>
					</p>
				)}
			</div>

			{dados === null ? (
				<div className="flex justify-center py-10">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			) : modulos.length === 0 ? (
				<div className="rounded-xl border bg-card py-12 text-center">
					<PackageOpen className="mx-auto h-8 w-8 text-muted-foreground/50" />
					<p className="mt-3 text-sm font-medium">Nenhum módulo instalado</p>
					<p className="mt-1 text-xs text-muted-foreground">
						Suba o pacote do módulo (ex.: Ligações de voz) para ativá-lo neste
						servidor. Depois libere para as empresas na aba “À venda”.
					</p>
				</div>
			) : (
				<div className="grid gap-3 lg:grid-cols-2">
					{modulos.map((m) => {
						const estado = ESTADOS[m.situacao?.estado] || ESTADOS.parado;
						const carregandoAcao = (c) => ocupado === `${m.key}:${c}`;
						return (
							<div key={m.key} className="flex flex-col rounded-xl border bg-card p-4">
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<p className="truncate font-medium">{m.name}</p>
										<p className="text-xs text-muted-foreground">
											<code>{m.key}</code> · versão <code>{m.version}</code>
										</p>
									</div>
									<span className="flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium">
										<span className={cn("h-2 w-2 rounded-full", estado.cor)} />
										{m.enabled ? estado.rotulo : "Desativado"}
									</span>
								</div>

								{m.description && (
									<p className="mt-2 text-xs leading-relaxed text-muted-foreground">
										{m.description}
									</p>
								)}

								{m.situacao?.ultimoErro && m.situacao.estado !== "rodando" && (
									<p className="mt-2 break-words rounded-md bg-destructive/10 px-2 py-1.5 font-mono text-[11px] text-destructive">
										{m.situacao.ultimoErro}
									</p>
								)}

								<dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
									<dt>Atualizado em</dt>
									<dd className="text-foreground">{dataHora(m.updatedAt)}</dd>
									<dt>No ar desde</dt>
									<dd className="text-foreground">
										{m.situacao?.estado === "rodando" ? dataHora(m.situacao.desde) : "—"}
									</dd>
									<dt>Reinícios seguidos</dt>
									<dd className="text-foreground">{m.situacao?.reinicios ?? 0}</dd>
									{m.previousVersion && (
										<>
											<dt>Versão anterior</dt>
											<dd className="text-foreground">
												<code>{m.previousVersion}</code>
											</dd>
										</>
									)}
									{m.license && (
										<>
											<dt>Licença</dt>
											<dd className="text-foreground">
												{m.source ? (
													<a
														className="underline underline-offset-2"
														href={m.source}
														target="_blank"
														rel="noreferrer"
													>
														{m.license} · código-fonte
													</a>
												) : (
													m.license
												)}
											</dd>
										</>
									)}
								</dl>

								<div className="mt-3 flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
									<ShieldCheck className="h-3.5 w-3.5" />
									Assinatura conferida na instalação
								</div>

								<div className="mt-3 flex flex-wrap gap-1.5 border-t pt-3">
									{m.enabled ? (
										<Button
											variant="outline"
											size="sm"
											disabled={!!ocupado}
											onClick={() => acao(m, "disable", `${m.name} desativado.`)}
										>
											{carregandoAcao("disable") ? (
												<Loader2 className="h-3.5 w-3.5 animate-spin" />
											) : (
												<PowerOff className="h-3.5 w-3.5" />
											)}
											Desativar
										</Button>
									) : (
										<Button
											size="sm"
											disabled={!!ocupado}
											onClick={() => acao(m, "enable", `${m.name} ativado.`)}
										>
											{carregandoAcao("enable") ? (
												<Loader2 className="h-3.5 w-3.5 animate-spin" />
											) : (
												<Power className="h-3.5 w-3.5" />
											)}
											Ativar
										</Button>
									)}
									{m.enabled && (
										<Button
											variant="outline"
											size="sm"
											disabled={!!ocupado}
											onClick={() => acao(m, "restart", "Reiniciado.")}
										>
											{carregandoAcao("restart") ? (
												<Loader2 className="h-3.5 w-3.5 animate-spin" />
											) : (
												<RefreshCw className="h-3.5 w-3.5" />
											)}
											Reiniciar
										</Button>
									)}
									<Button variant="outline" size="sm" onClick={() => verLogs(m)}>
										<ScrollText className="h-3.5 w-3.5" />
										Log
									</Button>
									{m.previousVersion && (
										<Button
											variant="outline"
											size="sm"
											disabled={!!ocupado}
											onClick={() =>
												acao(m, "rollback", `Voltou para ${m.previousVersion}.`)
											}
										>
											<History className="h-3.5 w-3.5" />
											Voltar versão
										</Button>
									)}
									<Button
										variant="ghost"
										size="sm"
										className="ml-auto text-destructive hover:text-destructive"
										onClick={() => setRemover(m)}
									>
										<Trash2 className="h-3.5 w-3.5" />
										Remover
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<Dialog open={!!logs} onOpenChange={(v) => !v && setLogs(null)}>
				<DialogContent className="sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle>Log — {logs?.modulo?.name}</DialogTitle>
						<DialogDescription>
							Últimas {logs?.linhas?.length || 0} linhas do processo (some ao reiniciar
							a API).
						</DialogDescription>
					</DialogHeader>
					<pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 font-mono text-[11px] leading-relaxed">
						{logs?.linhas?.length ? logs.linhas.join("\n") : "Sem linhas ainda."}
					</pre>
					<DialogFooter>
						<Button variant="outline" onClick={() => verLogs(logs.modulo)}>
							<RotateCcw className="h-4 w-4" />
							Atualizar
						</Button>
						<Button onClick={() => setLogs(null)}>Fechar</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={!!remover} onOpenChange={(v) => !v && setRemover(null)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Remover {remover?.name}?</DialogTitle>
						<DialogDescription>
							O processo para e os arquivos do módulo saem do servidor. As telas que
							dependem dele deixam de funcionar para todas as empresas.
						</DialogDescription>
					</DialogHeader>
					<label className="flex items-start gap-2 rounded-lg border p-3 text-sm">
						<Checkbox
							checked={apagarDados}
							onCheckedChange={(v) => setApagarDados(Boolean(v))}
							className="mt-0.5"
						/>
						<span>
							Apagar também os dados do módulo
							<span className="block text-xs text-muted-foreground">
								Chave interna e gravações pendentes. Sem marcar, reinstalar depois
								recupera tudo. Aparelhos pareados ficam no banco do motor.
							</span>
						</span>
					</label>
					<DialogFooter>
						<Button variant="outline" onClick={() => setRemover(null)}>
							Cancelar
						</Button>
						<Button variant="destructive" onClick={confirmarRemocao}>
							Remover
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default Instalados;
